import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LifeBuoy, ShieldAlert, Mail, FileText } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { useSEO } from "@/hooks/useSEO";

const CARDS = [
  { icon: <LifeBuoy className="h-5 w-5" />, title: "General Support", desc: "Questions about using IND RIL, your account or the app.", note: "Use in-app Help, or reach us via the contact channel published before launch." },
  { icon: <ShieldAlert className="h-5 w-5" />, title: "Safety & Grievance", desc: "Report safety concerns, content issues or file a grievance.", note: "Serious safety issues are prioritised. A Grievance Officer contact will be published per applicable Indian law." },
  { icon: <FileText className="h-5 w-5" />, title: "Legal & Privacy", desc: "Requests related to privacy, data or legal notices.", note: "See our Privacy Policy and Grievance Policy for the process and rights." },
];

export default function Support() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useSEO({ title: "Support & Grievance | IND RIL", description: "Get help with IND RIL, report a safety issue, or file a grievance.", path: "/support" });

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Header />
      <main className="mx-auto max-w-4xl px-6 pt-32 pb-24">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-white/50 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to home</Link>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="text-xs uppercase tracking-[0.3em] text-indril-saffron">Support &amp; Grievance</span>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">We're here to help.</h1>
          <p className="mt-4 max-w-2xl text-white/60">Choose the right channel below. For account actions, please use the IND RIL app.</p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {CARDS.map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
              className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-xl">
              <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-indril-saffron/15 text-indril-saffron">{c.icon}</span>
              <h3 className="font-display text-lg font-semibold">{c.title}</h3>
              <p className="mt-2 text-sm text-white/60">{c.desc}</p>
              <p className="mt-3 text-xs text-white/40">{c.note}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-4 text-sm">
          <Link to="/legal/grievance" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-white hover:border-indril-saffron/50"><Mail className="h-4 w-4" /> Grievance Policy</Link>
          <Link to="/legal/privacy" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-white hover:border-indril-saffron/50">Privacy Policy</Link>
          <Link to="/legal/account-deletion" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-white hover:border-indril-saffron/50">Account Deletion</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
